import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ResultType } from '../../../../models/enums/result-type.enum';
import { ManagementApiService } from '../../../../services';
import { SubHeaderData } from '../../../../models/sub-header-data';
import { UserSettingModel } from '../../models/informate';
import { UserUtilityService } from 'src/app/services/user-utility.service';
import { userActions } from 'src/app/models/enums/user-permissions.enum';
import { Subscription } from 'rxjs';
import { InstanceDataService } from '../../../../services/instance-data.service';
import { CustomToastService } from 'src/app/services/custom-toast.service';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { User } from 'src/app/Modules/management/models/organization/user.model';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'app-settings-informate',
    templateUrl: './settings-informate.component.html',
    styleUrls: ['./settings-informate.component.scss'],
})
export class SettingsInformateComponent implements OnInit {
    subHeaderData: SubHeaderData;
    userSettings: UserSettingModel[];
    defaultUnitSettings: UserSettingModel[];
    calculationPreferenceSettings: UserSettingModel[];
    isInstanceConfAllowed: boolean = false;
    isSaveEnabled: boolean = false;
    public informateSettingsForm: UntypedFormGroup;
    result: ResultType = ResultType.Error;
    tooltip_position = 'above';
    saveAllowedItems: boolean;
    curInstanceId: string;
    viewUserDefults: boolean;
    viewCalculationPreference: boolean;
    profileSettingAreaId: string;
    isLoaded: boolean = false;
    CurrentUserSub: Subscription;

    constructor(
        private fb: UntypedFormBuilder,
        private mgmtApi: ManagementApiService,
        private instanceDataService: InstanceDataService,
        private cd: ChangeDetectorRef,
        private user_utility: UserUtilityService,
        private customToastService: CustomToastService,
        private ngxUiLoaderService: NgxUiLoaderService,
                 private translocoService: TranslocoService
    ) {}

    async ngOnInit(): Promise<void> {
        this.curInstanceId = localStorage.getItem('currentInstanceId');
        this.subHeaderData = this.setSubHeaderValues();
        await this.validateUserLevel();
        this.CurrentUserSub = this.instanceDataService.userDetails.subscribe(async (user: User) => {
            if (!this.isLoaded) {
                await this.validateUserLevel();
            }
        });
    }

    async validateUserLevel() {
        const res = await this.user_utility.checkUserPermissionDataAndRoute('informate', userActions.View);
        if (res) {
            this.getSettings();
        }

        if (this.mgmtApi.userDetails) {
            this.isInstanceConfAllowed = await this.user_utility.checkUserPermissionDataAndRoute('InformateConfiguration', userActions.View);
            this.isLoaded = true;
        }

        this.viewUserDefults = await this.user_utility.checkUserPermissionDataAndRoute('settings-defaults', userActions.View);
        this.viewCalculationPreference = await this.user_utility.checkUserPermissionDataAndRoute('settings-calculation-preferences', userActions.View);
    }

    async getSettings() {
        this.ngxUiLoaderService.start();
        try {
            let res = await this.mgmtApi.getSettingProfileAreaId('informate').toPromise();
            if (res.success === true) {
                this.profileSettingAreaId = res.response.settingAreaId;
                if (this.profileSettingAreaId) {
                    let userSettings = await this.mgmtApi.getSettingbySettinfAreaId(this.profileSettingAreaId).toPromise();
                    if (userSettings.success === true) {
                        this.userSettings = userSettings.response;
                        this.buildForm();
                        this.defaultUnitSettings = this.getSectionData('Defaults', false);
                        this.calculationPreferenceSettings = this.getSectionData('Calculation Preferences', false);
                        this.cd.markForCheck();
                    } else {
                        this.user_utility.errorModelPopup(userSettings.message);
                    }
                    this.ngxUiLoaderService.stop();
                }
            } else {
                this.user_utility.errorModelPopup(res.message);
            }
            this.ngxUiLoaderService.stop();
        } catch (err) {
            this.ngxUiLoaderService.stop();
            this.user_utility.errorModelPopup(err.message);
        }
    }

    buildForm(): void {
        this.informateSettingsForm = null;
        this.informateSettingsForm = this.fb.group({});

        this.userSettings.forEach((obj, index) => {
            const validatorsToAdd = [];

            if (obj.setting.isRequired) {
                validatorsToAdd.push(Validators.required);
            }

            this.informateSettingsForm.addControl(obj.setting.key, this.fb.control(this.getFormStateValue(obj), validatorsToAdd));
        });
    }

    getFormStateValue(userSetting: UserSettingModel) {
        if (userSetting.value) {
            return userSetting.value;
        }

        if (userSetting.settingValue) {
            return userSetting.settingValue.settingValueId;
        }

        return null;
    }

    getSectionData(sectionName: string, isSubSection: boolean = false): UserSettingModel[] {
        const sections: UserSettingModel[] = this.userSettings.filter((item: UserSettingModel) => {
            if (isSubSection) {
                return item.setting.settingSubSection?.name.toLowerCase() === sectionName.toLowerCase();
            }

            return item.setting.settingSubSection?.settingSection.name.toLowerCase() === sectionName.toLowerCase();
        });

        return sections;
    }

    setSubHeaderValues(): SubHeaderData {
        let headerData = new SubHeaderData();
        headerData.displayText = this.translocoService.translate('SETTING_PAGE.SETTING_SUB_HEADER.Settings');
        headerData.disableActions = true;
        headerData.defaultImageSrc = '../../../../../../assets/media/logos/settings-black.png';
        headerData.mouseOverImageSrc = '../../../../../../assets/media/logos/settings-yellow.png';
        return headerData;
    }

    enableSaveButtonHandler(value: boolean) {
        this.subHeaderData.isInstanceSettings = true;
        this.isSaveEnabled = value;
    }

    async saveSettingsOutputHandler(saveSettings: boolean) {
        if (this.user_utility.noneSelectedPassPct) {
            this.customToastService.infoToast('Atleast one pass must be selected!', 'Info');
            this.informateSettingsForm.markAsDirty();
            this.informateSettingsForm.markAsTouched();
            this.informateSettingsForm.updateValueAndValidity();

            return this.informateSettingsForm;
        }
        if (saveSettings) {
            // informate form...
            if (this.informateSettingsForm) {
                const updateJson = [];

                Object.keys(this.informateSettingsForm.controls).forEach((key) => {
                    const ctrl: AbstractControl = this.informateSettingsForm.controls[key];
                    const json = {};

                    if (ctrl.dirty && ctrl.valid) {
                        const userSetting: UserSettingModel = this.userSettings.find((item) => item.setting.key === key);

                        if (userSetting) {
                            json['userSettingId'] = userSetting.userSettingId;
                            json['userId'] = userSetting.userId;
                            json['settingId'] = userSetting.setting.settingId;
                            if (userSetting.settingValue) {
                                json['settingValueId'] = ctrl.value; //userSetting.settingValue.settingValueId
                            } else if (userSetting.value) {
                                json['value'] = ctrl.value; //userSetting.value
                            }

                            updateJson.push(json);
                        }
                    }
                });

                // update mgmt service!
                if (updateJson.length !== 0) {
                    await this.updateSettingsInformate(updateJson);
                }
            }
        }
    }

    async saveInstanceSettingsOutputHandler(saveSettings: boolean) {
        //save Allowed Items
        this.saveAllowedItems = true;
    }

    async updateSettingsInformate(updateJson) {
        try {
            this.ngxUiLoaderService.start();
            let result = await this.mgmtApi.updateUserSettings(updateJson).toPromise();
            if (result.success === true) {
                await this.mgmtApi.refreshUserDefaults();
                await this.getSettings();
                this.ngxUiLoaderService.stop();
                this.customToastService.successToast('User settings updated!', 'Success');
            } else {
                this.ngxUiLoaderService.stop();
                this.customToastService.errorToast('Unable to update settings...', 'Failure');
            }
        } catch (err) {
            this.ngxUiLoaderService.stop();
            this.customToastService.errorToast('Unable to update settings...', 'Failure');
        }
    }
}
